import {
  InfiniteData,
  QueryClient,
  useInfiniteQuery,
} from '@tanstack/react-query';
import {
  listThreads,
  ThreadPage,
  ThreadStatus,
  ThreadSummary,
} from '@/experiences/sales-portal/domain/superintelligence/blocs/services/thread-api';
import { threadRunState } from '@/experiences/sales-portal/domain/superintelligence/blocs/utils/thread-list-groups';
import { useCallback, useMemo } from 'react';

const THREAD_LIST_STALE_TIME = 30 * 1000;
const RUNNING_THREAD_POLL_INTERVAL = 10 * 1000;

export const THREAD_LIST_QUERY_KEY = ['superintelligence', 'threads'] as const;

export const threadListQueryKey = (status: ThreadStatus) =>
  [...THREAD_LIST_QUERY_KEY, status] as const;

type ThreadListData = InfiniteData<ThreadPage, string | null>;

const hasRunningThread = (data: ThreadListData | undefined) =>
  data?.pages.some((page) =>
    page.items.some((thread) => threadRunState(thread) === 'running'),
  ) ?? false;

export interface ThreadListState {
  error: Error | null;
  hasMore: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
  loadMore: () => void;
  refetch: () => void;
  threads: readonly ThreadSummary[];
}

export const useThreadListQuery = (
  status: ThreadStatus,
  enabled = true,
): ThreadListState => {
  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    refetch,
  } = useInfiniteQuery({
    enabled,
    getNextPageParam: (lastPage: ThreadPage) => lastPage.nextCursor,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) =>
      listThreads({ cursor: pageParam, signal, status }),
    queryKey: threadListQueryKey(status),
    refetchInterval: (query) =>
      hasRunningThread(query.state.data) ? RUNNING_THREAD_POLL_INTERVAL : false,
    staleTime: THREAD_LIST_STALE_TIME,
  });

  const threads = useMemo(
    () => data?.pages.flatMap((page) => page.items) ?? [],
    [data],
  );

  const loadMore = useCallback(() => {
    void fetchNextPage();
  }, [fetchNextPage]);
  const refetchList = useCallback(() => {
    void refetch();
  }, [refetch]);

  return useMemo(
    () => ({
      error: error ?? null,
      hasMore: hasNextPage,
      isLoading,
      isLoadingMore: isFetchingNextPage,
      loadMore,
      refetch: refetchList,
      threads,
    }),
    [
      error,
      hasNextPage,
      isFetchingNextPage,
      isLoading,
      loadMore,
      refetchList,
      threads,
    ],
  );
};

export const invalidateThreadLists = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({ queryKey: THREAD_LIST_QUERY_KEY });

export const cancelThreadLists = (queryClient: QueryClient) =>
  queryClient.cancelQueries({ queryKey: THREAD_LIST_QUERY_KEY });

export const updateCachedThread = (
  queryClient: QueryClient,
  threadId: string,
  update: (thread: ThreadSummary) => ThreadSummary | null,
) => {
  queryClient.setQueriesData<ThreadListData>(
    { queryKey: THREAD_LIST_QUERY_KEY },
    (data) =>
      data && {
        ...data,
        pages: data.pages.map((page) => ({
          ...page,
          items: page.items.flatMap((thread) => {
            if (thread.threadId !== threadId) {
              return [thread];
            }
            const next = update(thread);
            return next ? [next] : [];
          }),
        })),
      },
  );
};
